import { parseISO, isValid as isValidDate, differenceInYears } from 'date-fns';
import { calculateNetDiscountRate, calculateCustomEndOfLife, calculateCustomRetirementDate } from './financialCalculations';
import { calculateWorklifeExpectancy } from './worklifeExpectancyCalculator';
import { calculateEndOfLife } from './lifeExpectancyCalculator';
import { isValidDateOfBirth, parseDate } from './utils';

/**
 * A centralized function to update all calculated fields for a report.
 * It handles both statistical and custom value calculations.
 * @param {object} reportData The full report data object.
 * @returns {Promise<object>} A promise that resolves to an object containing the updated fields.
 */
export const updateCalculatedValues = async (newData, oldData) => {
    const reportData = newData; // for clarity within the function
    const {
        dateOfBirth,
        gender,
        country,
        provinceOrState,
        educationLevel,
        employmentStatus,
        claimantSector,
        discountRate,
        growthRate,
        useCustomValues,
        valuationDate,
        worklifeExpectancyYears, // custom value
        lifeExpectancyYears,   // custom value
    } = reportData;

    const updatedFields = {};

    // 1. Calculate Net Discount Rate (common to both modes)
    const newNetDiscountRate = calculateNetDiscountRate(discountRate, growthRate);
    if (newNetDiscountRate !== null) {
        updatedFields.netDiscountRate = newNetDiscountRate;
    }

    const dob = parseDate(dateOfBirth);

    if (dob && isValidDateOfBirth(dob)) {
        const dobString = dob.toISOString().split('T')[0];

        if (useCustomValues) {
            // 2a. Custom Calculations
            const valuationDateObj = parseDate(valuationDate);
            if (isValidDate(valuationDateObj)) {
                const ageAtValuation = differenceInYears(valuationDateObj, dob);

                const worklife = parseFloat(worklifeExpectancyYears);
                if (!isNaN(worklife)) {
                    updatedFields.retirementAge = ageAtValuation + worklife;
                    // Recalculate retirementDate when worklifeExpectancyYears changes
                    const newRetirementDate = calculateCustomRetirementDate(worklife, valuationDate);
                    if (newRetirementDate) {
                        updatedFields.retirementDate = newRetirementDate;
                    }
                }

                const lifeExpectancy = parseFloat(lifeExpectancyYears);
                if (!isNaN(lifeExpectancy)) {
                    updatedFields.lifespanAge = ageAtValuation + lifeExpectancy;
                    // Recalculate expectedEndOfLife when lifeExpectancyYears changes
                    const newEndOfLife = calculateCustomEndOfLife(lifeExpectancy, valuationDate);
                    if (newEndOfLife) {
                        updatedFields.expectedEndOfLife = newEndOfLife;
                    }
                }
            }
        } else {
            // 2b. Statistical Calculations
            const worklifeResult = await calculateWorklifeExpectancy(dobString, gender, educationLevel, country, employmentStatus, claimantSector);
            if (worklifeResult && worklifeResult.retirementDate) {
                const retirementDate = parseISO(worklifeResult.retirementDate);
                if (isValidDate(retirementDate)) {
                    updatedFields.worklifeExpectancyYears = worklifeResult.worklifeYears;
                    updatedFields.retirementDate = worklifeResult.retirementDate;
                    updatedFields.retirementAge = differenceInYears(retirementDate, dob);
                }
            }

            const lifeResult = await calculateEndOfLife(dobString, gender, country, provinceOrState);
            if (lifeResult && lifeResult.endDate) {
                const endDate = parseISO(lifeResult.endDate);
                if (isValidDate(endDate)) {
                    updatedFields.lifeExpectancyYears = lifeResult.expectancyYears;
                    updatedFields.lifespanAge = differenceInYears(endDate, dob);
                    updatedFields.expectedEndOfLife = lifeResult.endDate;
                }
            }
        }
    }

    // 3. Rounding and final check for changes (to avoid unnecessary re-renders)
    const finalUpdatedFields = {};
    const fieldsToRound = ['worklifeExpectancyYears', 'retirementAge', 'lifeExpectancyYears', 'lifespanAge'];
    const dateFields = ['retirementDate', 'expectedEndOfLife'];

    const round = (num) => {
        if (typeof num !== 'number') return num;
        return Math.round((num + Number.EPSILON) * 100) / 100;
    }

    for (const key in updatedFields) {
        const dataToCompare = oldData ?? reportData;
        const oldValue = dataToCompare[key];
        let newValue = updatedFields[key];

        if (fieldsToRound.includes(key)) {
            newValue = round(newValue);
            if (round(oldValue) !== newValue) {
                finalUpdatedFields[key] = newValue;
            }
        } else if (dateFields.includes(key)) {
            const oldDate = oldValue ? parseDate(oldValue)?.toISOString().split('T')[0] : null;
            const newDate = newValue ? parseDate(newValue)?.toISOString().split('T')[0] : null;
            if (oldDate !== newDate) {
                finalUpdatedFields[key] = newValue;
            }
        } else {
            if (oldValue !== newValue) {
                finalUpdatedFields[key] = newValue;
            }
        }
    }
    return finalUpdatedFields;
};